import { useThree, useFrame } from '@react-three/fiber';
import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useConversation } from '@/services/conversationService';
import { useAuth } from '@/contexts/AuthContext';

interface ThumbnailCaptureProps {
    geometry: THREE.BufferGeometry | null;
}

export function ThumbnailCapture({ geometry }: ThumbnailCaptureProps) {
    const { gl, scene, camera } = useThree();
    const { conversation } = useConversation();
    const { user } = useAuth();
    const hasCapturedRef = useRef<string | null>(null);
    const frameCountRef = useRef(0);

    const captureAndUpload = useCallback(async () => {
        if (!user?.id || !conversation?.id) return;

        // Avoid capturing the same geometry twice
        const geometryId = geometry?.uuid;
        if (hasCapturedRef.current === geometryId) return;

        try {
            // Render the scene to capture current state
            gl.render(scene, camera);

            // Get the canvas data as a blob
            const dataUrl = gl.domElement.toDataURL('image/png', 0.8);

            // Convert data URL to blob
            const response = await fetch(dataUrl);
            const blob = await response.blob();

            // Upload to Supabase storage
            const thumbnailPath = `${user.id}/${conversation.id}/thumbnail.png`;

            // Try to delete existing thumbnail first (ignore errors)
            await supabase.storage.from('images').remove([thumbnailPath]);

            // Upload the new thumbnail
            const { error } = await supabase.storage
                .from('images')
                .upload(thumbnailPath, blob, {
                    contentType: 'image/png',
                    upsert: true
                });

            if (error) {
                console.log('Thumbnail upload error:', error.message);
            } else {
                console.log('Thumbnail saved for conversation:', conversation.id);
                hasCapturedRef.current = geometryId || null;
            }
        } catch (e) {
            console.log('Error capturing thumbnail:', e);
        }
    }, [gl, scene, camera, user?.id, conversation?.id, geometry?.uuid]);

    // Wait a few frames for the scene to fully render, then capture
    useFrame(() => {
        if (geometry && frameCountRef.current < 30) {
            frameCountRef.current++;
        } else if (geometry && frameCountRef.current === 30) {
            frameCountRef.current++; // Prevent re-triggering
            captureAndUpload();
        }
    });

    // Reset frame count when geometry changes
    useEffect(() => {
        if (geometry) {
            frameCountRef.current = 0;
            hasCapturedRef.current = null;
        }
    }, [geometry]);

    return null; // This component doesn't render anything
}
